// frontend/src/pages/manufacturing/v2/components/AdminWODeleteDialogV2.jsx
//
// Admin-only HARD-DELETE dialog for a v2 work order. This is distinct from the
// shop-floor "cancel" flow — it physically removes work orders and reverses v1
// writebacks/reservations/order-status.
//
// Two modes, driven by wo.is_top_level:
//   - Top-level WO: deletes the ENTIRE tree. High-risk, so we require the admin
//     to type the WO number to confirm before enabling the button. Calls
//     deleteWorkOrder(id, { cascade: 'tree' }) and shows the returned report.
//   - Leaf WO: plain confirm. Calls deleteWorkOrder(id). If the backend 400s
//     (e.g. it's actually a top-level WO or has dependents), we surface the
//     message and offer a "Delete whole tree instead" escalation.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Alert, Box, Typography, Stack, CircularProgress, List, ListItem, ListItemText,
} from '@mui/material';
import { Delete, Warning } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

function extractError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Delete failed';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  const firstKey = Object.keys(data)[0];
  if (firstKey) {
    const v = data[firstKey];
    return Array.isArray(v) ? v.join(' ') : String(v);
  }
  return 'Delete failed';
}

// Turn the cascade report into readable "N work orders, M movements…" lines.
const REPORT_LABELS = {
  deleted_work_orders: 'Work orders deleted',
  movements_deleted: 'Movements deleted',
  writebacks_reversed: 'Writebacks reversed',
  reservations_released: 'Reservations released',
  order_status_reverted: 'Order statuses reverted',
  stock_levels_recalculated: 'Stock levels recalculated',
};

export default function AdminWODeleteDialogV2({ open, wo, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  // When a leaf delete 400s, offer escalation to a full-tree delete.
  const [offerTree, setOfferTree] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmText('');
    setDeleting(false);
    setError(null);
    setReport(null);
    setOfferTree(false);
  }, [open, wo]);

  if (!wo) return null;

  const isTopLevel = !!wo.is_top_level;
  const treeCount = wo.tree_work_order_count ?? wo.descendant_count ?? null;
  const confirmed = confirmText.trim() === String(wo.wo_number);

  const runDelete = async (cascade) => {
    setDeleting(true);
    setError(null);
    try {
      const res = await v2.workOrders.delete(wo.id, cascade ? { cascade: 'tree' } : {});
      // Cascade returns a report object; leaf delete may return 204/empty.
      if (cascade && res?.data && typeof res.data === 'object') {
        setReport(res.data);
      } else {
        if (onDeleted) onDeleted(res?.data || null);
        onClose();
      }
    } catch (err) {
      console.error('Hard-delete failed', err);
      setError(extractError(err));
      // A leaf delete that fails because the WO is top-level / has dependents
      // can be escalated to a full-tree delete.
      if (!cascade) setOfferTree(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleReportClose = () => {
    if (onDeleted) onDeleted(report);
    onClose();
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete /> Delete Work Order: {wo.wo_number}
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Success report after a tree delete */}
        {report ? (
          <Box data-testid="delete-report" sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Tree deleted successfully.
            </Alert>
            <List dense disablePadding>
              {Object.keys(REPORT_LABELS)
                .filter((k) => report[k] != null)
                .map((k) => (
                  <ListItem key={k} disableGutters>
                    <ListItemText primary={`${REPORT_LABELS[k]}: ${report[k]}`} />
                  </ListItem>
                ))}
            </List>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }} data-testid="delete-error">
                {error}
              </Alert>
            )}
            {isTopLevel ? (
              <>
                <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                  <Typography variant="body2">
                    <strong>This deletes the ENTIRE tree</strong>
                    {treeCount != null ? ` (${treeCount} work orders)` : ''}. All stock
                    writebacks, reservations, and order-status changes made by this
                    tree will be reversed. This cannot be undone.
                  </Typography>
                </Alert>
                <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                  Type the work order number <strong>{wo.wo_number}</strong> to confirm:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={wo.wo_number}
                  inputProps={{ 'data-testid': 'delete-confirm-text' }}
                />
              </>
            ) : (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Alert severity="warning" icon={<Warning />}>
                  <Typography variant="body2">
                    Permanently delete this work order? This cannot be undone.
                  </Typography>
                </Alert>
                {offerTree && (
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => runDelete(true)}
                    disabled={deleting}
                    data-testid="delete-escalate-tree"
                  >
                    Delete whole tree instead
                  </Button>
                )}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {report ? (
          <Button onClick={handleReportClose} variant="contained" data-testid="delete-report-close">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={deleting}>Cancel</Button>
            <Button
              onClick={() => runDelete(isTopLevel)}
              variant="contained"
              color="error"
              startIcon={deleting ? <CircularProgress size={18} /> : <Delete />}
              disabled={deleting || (isTopLevel && !confirmed)}
              data-testid="delete-submit"
            >
              {isTopLevel ? 'Delete entire tree' : 'Delete work order'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
